import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const industries = [
  {
    name: "Warehousing & Distribution",
    desc: "Optimize warehouse operations with PVC dividing walls, sliding curtains, and temperature control solutions. Our products help create efficient workflow zones, separate cold storage areas, and maintain dust-free environments.",
    img: "https://customer-assets.emergentagent.com/job_industrial-textiles-1/artifacts/upna8xsp_IMG_0938.JPG",
    solutions: ["PVC Dividing Walls", "Sliding Curtains", "Temperature Barriers", "Dust Containment"]
  },
  {
    name: "Logistics & Transport",
    desc: "Protect loading docks and transit areas with weather protection systems and heavy-duty curtains. Keep goods safe during loading while maintaining efficient vehicle access.",
    img: "https://images.unsplash.com/photo-1645736315000-6f788915923b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBmb3JrbGlmdHxlbnwwfHx8fDE3Nzk2Nzc0MjR8MA&ixlib=rb-4.1.0&q=85",
    solutions: ["Loading Dock Curtains", "Weather Protection", "Vehicle Wash Curtains", "Containment Systems"]
  },
  {
    name: "Mining & Resources",
    desc: "Rugged containment and protection solutions built for the mining sector. From dust suppression curtains to equipment covers, our products are engineered for extreme conditions.",
    img: "https://images.unsplash.com/photo-1587919968590-fbc98cea6c9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxtaW5pbmclMjBvcGVyYXRpb25zfGVufDB8fHx8MTc3OTY3NzI4OHww&ixlib=rb-4.1.0&q=85",
    solutions: ["Dust Suppression", "Equipment Covers", "Containment Walls", "Weather Shelters"]
  },
  {
    name: "Manufacturing",
    desc: "Improve manufacturing environments with acoustic curtains, welding screens, and workspace dividers. Enhance safety, reduce noise, and create efficient production zones.",
    img: "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtYW51ZmFjdHVyaW5nJTIwZmFjdG9yeSUyMGZsb29yfGVufDB8fHx8MTc3OTY3NzQyNHww&ixlib=rb-4.1.0&q=85",
    solutions: ["Acoustic Curtains", "Welding Screens", "Workspace Dividers", "Safety Barriers"]
  },
  {
    name: "Agriculture",
    desc: "Durable shade structures, crop covers, and livestock shelter systems built to withstand harsh rural conditions. Protect livestock, crops, and equipment year-round.",
    img: "https://images.unsplash.com/photo-1638215640640-c51055587778?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhZ3JpY3VsdHVyZSUyMGZhcm0lMjBzaGVkfGVufDB8fHx8MTc3OTY3NzQyNHww&ixlib=rb-4.1.0&q=85",
    solutions: ["Shade Structures", "Livestock Shelters", "Crop Protection", "Equipment Covers"]
  },
  {
    name: "Commercial & Construction",
    desc: "Temporary and permanent partitioning solutions for commercial facilities and construction projects. Create weather-tight enclosures and safe work zones.",
    img: "https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwY29tbWVyY2lhbCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc3OTY3NzQyNHww&ixlib=rb-4.1.0&q=85",
    solutions: ["Site Enclosures", "Temporary Walls", "Weather Screens", "Dust Barriers"]
  },
];

export default function IndustriesPage() {
  return (
    <div data-testid="industries-page">
      {/* Hero */}
      <section className="relative py-32 sm:py-40 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} className="industrial-line mb-8" />
            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Industries <span className="gradient-text">We Serve</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base sm:text-lg text-[#A3A3A3] max-w-xl leading-relaxed">
              Trusted by leading businesses across Australia's core industrial sectors, 
              delivering solutions tailored to each industry's unique demands.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-0">
          {industries.map((ind, index) => (
            <motion.div
              key={ind.name}
              data-testid={`industry-${index}`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${
                index % 2 !== 0 ? "" : ""
              }`}
            >
              <motion.div
                variants={fadeUp}
                className={`${index % 2 !== 0 ? "lg:order-2" : ""}`}
              >
                <div className="overflow-hidden h-full">
                  <img
                    src={ind.img}
                    alt={ind.name}
                    className="w-full h-full min-h-[300px] object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </motion.div>
              <motion.div
                variants={fadeUp}
                className={`flex items-center ${index % 2 !== 0 ? "lg:order-1" : ""}`}
              >
                <div className="p-8 sm:p-12 lg:p-16 bg-[#141414]">
                  <div className="industrial-line mb-6" />
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                    {ind.name}
                  </h2>
                  <p className="text-[#A3A3A3] mb-6 leading-relaxed">{ind.desc}</p>
                  <div className="mb-8">
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                      Key Solutions
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {ind.solutions.map((s) => (
                        <span key={s} className="bg-[#0A0A0A] border border-[#2E2E2E] text-[#A3A3A3] text-xs px-3 py-1.5">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Link
                    to="/quote"
                    className="inline-flex items-center gap-2 text-[#FF5722] text-sm font-semibold hover:gap-3 transition-all"
                  >
                    Get a Quote <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
